import React, { useState } from 'react';
import classNames from 'classnames/bind';

import ServiceForm from '@/components/CustomTravel/ServiceForm';
import { DimmedContent, DimmedFooter, DimmedHeader } from '@/components/CustomTravel/DimmedPopup';
import DateRangeField from '@/components/CustomTravel/ServiceFormFields/DateRangeField';
import CheckboxField from '@/components/CustomTravel/ServiceFormFields/CheckboxField';
import { CustomTravelEstimatePopupStep, CustomTravelParamType } from '@/components/CustomTravel/EstimatePopup';
import { CustomTravelSchedule } from '@/atoms/customTravelFormState';
import usePopupHandler from '@/hooks/custom/usePopupHandler';
import RecommendDuration from './RecommendDuration';
import styles from './SelectDurationForm.module.scss';

const cx = classNames.bind(styles);

enum ScheduleType {
  Departure,
  Arrival,
}

interface SelectDurationFormProps {
  externalValues?: { tourSchedule?: CustomTravelSchedule };
}

const SelectDurationForm: React.FC<SelectDurationFormProps> = ({ externalValues }) => {
  const { handleBack, onSubmitNextStep, currentType } = usePopupHandler();
  const isAdvisorType = currentType === CustomTravelParamType.ADVISOR_TYPE;

  const [isCalendarsOpenedPair, setIsCalendarsOpenedPair] = useState([false, false]);
  const isCalendarOpened = isCalendarsOpenedPair.some((isOpened) => isOpened);

  const handleChangeCalendarsOpened = (index: number) => (isOpened: boolean) => {
    const _isCalendarsOpenedPair = [...isCalendarsOpenedPair];
    _isCalendarsOpenedPair[index] = isOpened;

    setIsCalendarsOpenedPair(_isCalendarsOpenedPair);
  };

  const handleSubmit = (_values: any) => {
    const values = {
      ..._values,
      tourSchedule: {
        ..._values?.tourSchedule,
        schedules: _values?.tourSchedule?.schedules?.filter((schedule: any) => schedule?.from && schedule?.to),
      },
    };

    onSubmitNextStep(values, CustomTravelEstimatePopupStep.SelectPassenger, { type: currentType });
  };

  return (
    <ServiceForm onSubmit={handleSubmit} className={cx('SelectDuration')} externalValues={externalValues}>
      <DimmedHeader>
        {!currentType && (
          <span className={cx('page')} data-event-nlog="imp" data-event-area-code="customtravel.popup.duration">
            <span className={cx('blind')}>현재</span>
            <b>{'3'}</b>
            <span className={cx('blind')}>전체</span>
            <span>{'5'}</span>
          </span>
        )}
        <span className={cx('title')}>여행 일정</span>
      </DimmedHeader>
      <DimmedContent className={cx(isCalendarOpened && 'single-content')}>
        <DateRangeField
          prefixLabel="1순위"
          startDateFieldName="tourSchedule.schedules.0.from"
          endDateFieldName="tourSchedule.schedules.0.to"
          required
          onChangeOpen={handleChangeCalendarsOpened(ScheduleType.Departure)}
          placeholder="여행기간을 선택하세요."
          className={cx(isCalendarsOpenedPair[ScheduleType.Arrival] && 'hidden')}
          nlogAreaCode="cus.selfschedule01"
          closeNlogAreaCode="cus.selfcalendarclose"
        />
        <DateRangeField
          prefixLabel="2순위"
          startDateFieldName="tourSchedule.schedules.1.from"
          endDateFieldName="tourSchedule.schedules.1.to"
          onChangeOpen={handleChangeCalendarsOpened(ScheduleType.Arrival)}
          placeholder="여행기간을 선택하세요."
          className={cx(isCalendarsOpenedPair[ScheduleType.Departure] && 'hidden')}
          nlogAreaCode="cus.selfschedule02"
          closeNlogAreaCode="cus.selfcalendarclose"
        />
        <CheckboxField
          name="tourSchedule.flexible"
          label="날짜 조정이 가능해요."
          className={cx('compromise', isCalendarOpened && 'hidden')}
          defaultValue={false}
          nlogAreaCode="cus.selfscheduleflex"
        />
        <RecommendDuration />
      </DimmedContent>
      {!isCalendarOpened && (
        <DimmedFooter>
          <div className={cx('progress')}>
            <button
              type="button"
              className={cx('prev')}
              onClick={handleBack(
                isAdvisorType
                  ? CustomTravelEstimatePopupStep.SelectGroupType
                  : CustomTravelEstimatePopupStep.SelectLocation,
                { type: currentType },
              )}
              data-event-nlog="click"
              data-event-area-code="cus.selfschedulebackpage"
            >
              {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '이전' : '취소'}
            </button>
            <button
              type="submit"
              className={cx('next')}
              data-event-nlog="click"
              data-event-area-code="cus.selfschedulenextpage"
            >
              {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '다음' : '수정하기'}
            </button>
          </div>
        </DimmedFooter>
      )}
    </ServiceForm>
  );
};

export default SelectDurationForm;
